import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Alert, Button, Col, Row } from 'react-bootstrap';

import { captureException, captureMessage } from '@sentry/node';
import cn from 'classnames';
import moment from 'moment';
import captureExceptionForClientSide from 'utils/captureExceptionForClientSide';
import currencyFormat from 'utils/curencyFormat';

import CrossReferenceBannerSectionCampaignContent from 'components/CrossReferenceBannerSectionCampaignContent/CrossReferenceBannerSectionCampaignContent';
import DonateNowSummary from 'components/DonateNowForm/DonateNowSteps/DonateNowSummary';
import DonationOptionGallery from 'components/DonationOptionGallery/DonationOptionGallery';
import DonationOptionGrid from 'components/DonationOptionGrid/DonationOptionGrid';
import HeadWithSocialMedia from 'components/HeadWithSocialMedia/HeadWithSocialMedia';
import HowToSendYourGiftForm from 'components/HowToSendYourGiftForm/HowToSendYourGiftForm';
import Page404Content from 'components/Page404Content/Page404Content';
import SectionWithContainer from 'components/PageBlocks/SectionWithContainer/SectionWithContainer';
import RowWithOffsetCol from 'components/RowWithOffsetCol/RowWithOffsetCol';
import { CartContext } from 'contexts/cartContext';
import DonationCampaignTypes from 'models/donationCampaignTypes';
import { IDonationOption } from 'models/donationOption';
import { Frequency } from 'models/frequency';
import { DonationOptionClientService } from 'services/donationOptionClientService';
import { fetcher } from 'services/httpRequestor';

import MainLayout from '../../../layouts/MainLayout';

interface DonationCampaignOptionProps {
  statusCode?: number;
  donationOption?: IDonationOption & {
    related_donation_options: IDonationOption[];
  };
  relatedDonationOptions?: IDonationOption[];
  host?: string;
  hideRelatedDonationOption: boolean;
}

// This gets called on every request
export const getServerSideProps: GetServerSideProps = async ({
  query,
  req,
}) => {
  const campaignService = new DonationOptionClientService(
    fetcher(req.headers.cookie),
  );

  const {
    slug: donation_campaign_name,
    donation_option_slug,
    ...getParams
  } = query;

  try {
    const { data: donationOption, status } =
      await campaignService.getOneByPageName(
        donation_campaign_name as string,
        donation_option_slug as string,
        {
          query: getParams,
        },
      );
    if (status === 404) {
      return {
        props: {
          statusCode: 404,
        },
        notFound: true,
      };
    }

    if (!donationOption) {
      captureMessage('System could not find donation campaign', {
        extra: { query: JSON.stringify(getParams), donation_option_slug },
      });
    }
    const relatedDonationOptions = donationOption?.related_donation_options
      ?.sort(() => (Math.random() > 0.5 ? 1 : -1))
      ?.slice(0, 4);

    // Pass data to the page via props
    return {
      props: {
        donation_option_slug,
        statusCode: status,
        donationOption,
        relatedDonationOptions,
        hideRelatedDonationOption:
          donationOption.donation_campaign?.hide_related_donation_option,
      },
    };
  } catch (error: any) {
    console.error('[ERROR]', error);
    captureException(error, { extra: { slug: donation_option_slug } });
    return {
      props: {
        donation_option_slug,
        statusCode: error.status,
      },
    };
  }
};

const DonationOptionPage: React.FC<DonationCampaignOptionProps> = ({
  statusCode,
  donationOption,
  relatedDonationOptions,
  hideRelatedDonationOption,
  host,
}) => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const isProductListCampaign =
    donationOption?.donation_campaign?.donation_campaign_type?.name ===
    DonationCampaignTypes.ProductList;

  const {
    actions: { goToCheckoutPage },
  } = useContext(CartContext);

  const handlePurchase = useCallback(
    async (
      value: any,
      asAGuest: boolean,
      createNewAccountTabActive: boolean,
    ) => {
      if (!donationOption) {
        return;
      }
      setError(undefined);
      setLoading(true);
      try {
        await goToCheckoutPage(
          {
            recipient_first_name: value.recipient_first_name,
            recipient_last_name: value.recipient_last_name,
            recipient_email: value.recipient_email,
            recipient_message: value.recipient_message,
            recipient_custom_to: value.recipient_custom_to,
            recipient_custom_from: value.recipient_custom_from,
            donation_fund_id: value?.donationFund?.id,
            donation_campaign_id: donationOption.donation_campaign_id,
            donation_option_id: donationOption.id,
            frequency: Frequency.OnceOff,
            delivery_option: value.delivery_option,
            quantity: 1,
            delivery_dt: moment(
              !value.delivery_now && value.delivery_dt
                ? value.delivery_dt
                : undefined,
            ).toISOString(),
          },
          asAGuest,
          createNewAccountTabActive,
        );
      } catch (errorRequest) {
        setError('Something wrong happened. Try again later');
        captureExceptionForClientSide(errorRequest);
        setLoading(false);
      }
    },
    [donationOption, goToCheckoutPage],
  );

  const renderTitle = useCallback(() => {
    return (
      <div className="mb-3 d-flex">
        <h1 className="mr-2 mb-0 heading heading-danger">
          {donationOption?.heading}
        </h1>
        <h3 className="text-danger h1 font-weight-bold mb-0 ml-3">
          {currencyFormat(donationOption?.amount).replace(/.00$/, '')}
        </h3>
      </div>
    );
  }, [donationOption?.amount, donationOption?.heading]);

  const campaignContent = useMemo(() => {
    return donationOption?.donation_campaign?.campaign_contents?.[0];
  }, [donationOption?.donation_campaign?.campaign_contents]);

  const donationCampaignItemName = useMemo(() => {
    switch (donationOption?.donation_campaign?.donation_campaign_type?.name) {
      case DonationCampaignTypes.GiftCard:
        return 'gifts';
      case DonationCampaignTypes.FestiveBauble:
        return 'baubles';
      case DonationCampaignTypes.ProductList:
        return 'items';
      case DonationCampaignTypes.InnovationFund:
        return 'fund';
      case DonationCampaignTypes.RegularDonation:
      default:
        return 'donations';
    }
  }, [donationOption?.donation_campaign?.donation_campaign_type?.name]);

  if (statusCode !== 200) {
    return <Page404Content />;
  }

  return (
    <>
      <HeadWithSocialMedia
        title={`${donationOption?.heading} - Surf Life Saving Australia`}
        description={donationOption?.heading}
        thumbnailURL={donationOption?.image?.url}
        url={`${host}/campaign/${router.query?.slug}/${router.query.donation_option_slug}`}
        published_time={donationOption?.donation_campaign?.published_datetime}
        keywords={donationOption?.donation_campaign?.keywords}
      />
      <MainLayout>
        <SectionWithContainer>
          <div className="d-block d-md-none">{renderTitle()}</div>
          <Row>
            {!!donationOption?.donation_option_galleries?.length && (
              <Col className="mb-3 mb-md-0" md={6}>
                <DonationOptionGallery
                  donationOptionGalleries={
                    donationOption?.donation_option_galleries
                  }
                  sliderClassname={cn({
                    'object-fit-scale-down max-h-60': isProductListCampaign,
                  })}
                />
              </Col>
            )}
            <Col md={6}>
              <div className="d-none d-md-block">{renderTitle()}</div>
              {donationOption?.description && (
                <div
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: donationOption?.description,
                  }}
                />
              )}
              {!isProductListCampaign ? (
                <>
                  <h2 className="mb-3 mt-5">
                    How do you want to send your gift?
                  </h2>
                  {error && <Alert variant="danger">{error}</Alert>}
                  <HowToSendYourGiftForm
                    previewImageURL={donationOption?.ecard_inside_image?.url}
                    loading={loading}
                    onSubmit={handlePurchase}
                  />
                </>
              ) : (
                <DonateNowSummary
                  donationOption={donationOption}
                  onStepChange={() => {
                    router.push(`/campaign/${router.query.slug}`);
                  }}
                  onPurchase={async (
                    value,
                    asAGuest,
                    createNewAccountTabActive,
                  ) => {
                    await handlePurchase(
                      value,
                      asAGuest,
                      createNewAccountTabActive,
                    );
                  }}
                  frequency={Frequency.OnceOff}
                  canChangeFrequency={false}
                />
              )}
            </Col>
          </Row>
        </SectionWithContainer>
        {relatedDonationOptions?.length && !hideRelatedDonationOption && (
          <SectionWithContainer>
            <RowWithOffsetCol className="mb-4">
              <h2 className="heading heading-danger">
                Other {donationCampaignItemName} you might like
              </h2>
            </RowWithOffsetCol>
            <DonationOptionGrid
              donationCampaignOptions={relatedDonationOptions}
              donationCampaignSlug={(router.query.slug || '') as string}
              imageClassName="object-fit-scale-down max-h-100px"
            />
            <div className="d-flex justify-content-center">
              <Link href={`/campaign/${router.query?.slug}`} passHref>
                <Button as="a" variant="danger" className="mt-3">
                  See more {donationCampaignItemName}
                </Button>
              </Link>
            </div>
          </SectionWithContainer>
        )}
        {campaignContent && (
          <CrossReferenceBannerSectionCampaignContent
            campaignContent={campaignContent}
          />
        )}
      </MainLayout>
    </>
  );
};

export default DonationOptionPage;
